import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, AlertCircle, FileText, Target, Cpu, Calendar, LayoutTemplate, Globe, Wand2, Bot, Pencil, RotateCcw, X } from 'lucide-react';
import { Button, TextField, DatePicker, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { talentPosts, talentTemplates, libraries, statuses, talents, accounts } from '../../services/apiService';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';

const DEFAULT_BLOCKS = {
  responsibilities: true,
  cv: true,
  media: false,
  languages: true,
  tools: true,
  professions: true
};

const BLOCK_META = {
  professions:      { label: 'Professions',      icon: '🎯' },
  responsibilities: { label: 'Responsibilities', icon: '📋' },
  tools:            { label: 'Tools & Skills',    icon: '🛠' },
  languages:        { label: 'Languages',         icon: '🌐' },
  cv:               { label: 'CV / Experience',   icon: '📁' },
  media:            { label: 'Media',             icon: '🖼' },
};

const LABEL = 'block text-[10px] font-bold uppercase tracking-widest text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5';

/* ── Section divider ─────────────────────────────────────────── */
const Divider = ({ icon: Icon, label, color = 'text-light-text-tertiary dark:text-dark-text-tertiary' }) => (
  <div className="flex items-center gap-2 my-1">
    <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
    <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${color}`}>
      {Icon && <Icon size={10} />}
      <span>{label}</span>
    </div>
    <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
);

/* ── Block toggle chip ───────────────────────────────────────── */
const BlockChip = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all duration-150 select-none ${
      active
        ? 'bg-blue-50 dark:bg-blue-900/25 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 shadow-sm'
        : 'bg-light-bg-primary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary hover:border-light-border-secondary dark:hover:border-dark-border-secondary'
    }`}
  >
    <span>{icon}</span>
    <span>{label}</span>
    {active && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-0.5 shrink-0" />}
  </button>
);

/* ── Collapsible term section ────────────────────────────────── */
const TermSection = ({ title, count, loading, open, onToggle, empty, children }) => (
  <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-primary transition-colors"
    >
      <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
        {title}
        {loading ? (
          <span className="ml-1.5 text-light-text-tertiary dark:text-dark-text-tertiary font-normal normal-case">loading…</span>
        ) : count > 0 ? (
          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-tertiary dark:text-dark-text-tertiary text-[10px] font-medium normal-case">{count}</span>
        ) : null}
      </span>
      {open ? <ChevronUp size={14} className="text-light-text-tertiary dark:text-dark-text-tertiary" /> : <ChevronDown size={14} className="text-light-text-tertiary dark:text-dark-text-tertiary" />}
    </button>
    {open && (
      loading ? (
        <div className="px-4 py-3 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Loading terms…</div>
      ) : empty ? (
        <div className="px-4 py-3 text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">No items found</div>
      ) : (
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {children}
        </div>
      )
    )}
  </div>
);

// Autocomplete requires string option values/selection; ids coming straight
// from the API (post, duplicateFrom, props) are numbers — normalize at the
// form-state boundary. Submission converts back via numOrNull below.
const strOrNull = (v) => (v != null ? String(v) : null);

const TalentPostModal = ({ isOpen, onClose, post, talentId, duplicateFrom, onSaved }) => {
  const isEdit = Boolean(post?.id);
  const isDuplicate = !isEdit && Boolean(duplicateFrom?.id);
  const fixedTalentId = strOrNull(talentId);

  const [form, setForm] = useState({
    talent_id: fixedTalentId, talent_template_id: null,
    name: '', slug: '', status_id: null,
    translation_id: null, inner_client_id: null, inner_client_iso: '',
    account_id: null, social_account_id: null,
    planned_date: '', end_date: '', discount: '',
    blocks_config: { ...DEFAULT_BLOCKS }, full_post: ''
  });
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingTalentData, setLoadingTalentData] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [talentOptions, setTalentOptions] = useState([]);
  const [innerClientOptions, setInnerClientOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [socialAccountOptions, setSocialAccountOptions] = useState([]);
  const [professionTerms, setProfessionTerms] = useState([]);
  const [responsibilityTerms, setResponsibilityTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [showProfessions, setShowProfessions] = useState(true);
  const [showResponsibilities, setShowResponsibilities] = useState(true);
  const [profChoices, setProfChoices] = useState({});
  const [respChoices, setRespChoices] = useState({});
  const [priceChoices, setPriceChoices] = useState({});
  const [pricesEditByProf, setPricesEditByProf] = useState({}); // { [profession_id]: bool }
  const [customProfIds, setCustomProfIds] = useState(new Set()); // profession_ids с кастомными ценами
  const priceSnapshot = useRef({}); // snapshot per profession_id for cancel
  const pricesChanged = useRef(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [rateOptions, setRateOptions] = useState([]);
  const [slugStatus, setSlugStatus] = useState(null);
  const [slugExisting, setSlugExisting] = useState(null); // { id, name } занятого поста
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [genFeedback, setGenFeedback] = useState('');
  const [showGenFeedback, setShowGenFeedback] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  // Lookup maps for name autogeneration (не вызывают ре-рендер)
  const innerClientsMap = useRef({});
  const accountsMap = useRef({});
  const socialAccountsMap = useRef({});
  const languagesMap = useRef({});
  const toolsMap = useRef({});
  const nameManuallyEdited = useRef(false); // флаг: пользователь вручную отредактировал Name
  const slugManuallyEdited = useRef(false); // флаг: пользователь вручную отредактировал Slug
  const [selectedTalentData, setSelectedTalentData] = useState(null);
  const requestGen = useRef(0);
  // Name/slug generation runs on the backend; the counter drops stale responses
  const identifiersTimer = useRef(null);
  const identifiersRequest = useRef(0);
  const [optionsVersion, setOptionsVersion] = useState(0);
  const refTemplate = useRef(null);
  const refTalent = useRef(null);
  const refName = useRef(null);
  const refSlug = useRef(null);
  const refStatus = useRef(null);
  const refInnerClient = useRef(null);
  const refTranslation = useRef(null);

  const scrollToRef = (ref) => ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  useEffect(() => {
    if (!isOpen) {
      nameManuallyEdited.current = false;
      slugManuallyEdited.current = false;
      pricesChanged.current = false;
      clearTimeout(slugCheckTimer.current);
      clearTimeout(identifiersTimer.current);
      identifiersRequest.current++;
      return;
    }
    if (isEdit && post) {
      setForm({
        talent_id: strOrNull(post.talent_id) || fixedTalentId || null,
        talent_template_id: strOrNull(post.talent_template_id),
        name: post.name || '', slug: post.slug || '',
        status_id: strOrNull(post.status_id),
        translation_id: strOrNull(post.translation_id),
        inner_client_id: strOrNull(post.inner_client_id),
        inner_client_iso: post.inner_client_iso || '',
        account_id: strOrNull(post.account_id), social_account_id: strOrNull(post.social_account_id),
        planned_date: post.planned_date ? post.planned_date.slice(0, 10) : '',
        end_date: post.end_date ? post.end_date.slice(0, 10) : '',
        discount: post.discount != null ? String(post.discount) : '',
        blocks_config: { ...DEFAULT_BLOCKS, ...(post.blocks_config || {}) },
        full_post: post.full_post || ''
      });
    } else if (isDuplicate && duplicateFrom) {
      setForm({
        talent_id: strOrNull(duplicateFrom.talent_id) || fixedTalentId || null,
        talent_template_id: strOrNull(duplicateFrom.talent_template_id),
        name: '', slug: '',
        status_id: strOrNull(duplicateFrom.status_id),
        translation_id: strOrNull(duplicateFrom.translation_id),
        inner_client_id: strOrNull(duplicateFrom.inner_client_id),
        inner_client_iso: duplicateFrom.inner_client_iso || '',
        account_id: strOrNull(duplicateFrom.account_id), social_account_id: strOrNull(duplicateFrom.social_account_id),
        planned_date: duplicateFrom.planned_date ? duplicateFrom.planned_date.slice(0, 10) : '',
        end_date: duplicateFrom.end_date ? duplicateFrom.end_date.slice(0, 10) : '',
        discount: duplicateFrom.discount != null ? String(duplicateFrom.discount) : '',
        blocks_config: { ...DEFAULT_BLOCKS, ...(duplicateFrom.blocks_config || {}) },
        full_post: duplicateFrom.full_post || ''
      });
    } else {
      setForm({
        talent_id: fixedTalentId, talent_template_id: null,
        name: '', slug: '', status_id: null,
        translation_id: null, inner_client_id: null, inner_client_iso: '',
        account_id: null, social_account_id: null,
        planned_date: '', end_date: '', discount: '',
        blocks_config: { ...DEFAULT_BLOCKS }, full_post: ''
      });
    }
    setSlugStatus(null); setSlugExisting(null);
    setProfessionTerms([]); setResponsibilityTerms([]);
    setProfChoices({}); setRespChoices({}); setPriceChoices({}); setPricesEditByProf({}); setCustomProfIds(new Set()); priceSnapshot.current = {};
    pricesChanged.current = false;
    setShowProfessions(true); setShowResponsibilities(true);
    setSelectedTalentData(null);
    nameManuallyEdited.current = isEdit ? true : false;
    slugManuallyEdited.current = isEdit ? true : false;
    setRevisions([]);
    setGenFeedback('');
    setShowGenFeedback(false);
    setSelectedModel(null);
    const gen = ++requestGen.current;
    loadOptions(gen);
  }, [isOpen, post, duplicateFrom]);

  useEffect(() => {
    const tid = form.talent_id;
    if (!tid || !isOpen) { setProfessionTerms([]); setResponsibilityTerms([]); setProfChoices({}); setRespChoices({}); setPriceChoices({}); setPricesEditByProf({}); setCustomProfIds(new Set()); priceSnapshot.current = {}; pricesChanged.current = false; return; }
    let cancelled = false;
    const isCancelled = () => cancelled;
    if (isEdit && post?.id && String(post.talent_id) === tid) loadTermsFromApi(post.id, isCancelled);
    else if (isDuplicate && duplicateFrom?.id && String(duplicateFrom.talent_id) === tid) loadTermsFromApi(duplicateFrom.id, isCancelled);
    else loadTermsFromTalent(tid, isCancelled);
    return () => { cancelled = true; };
  }, [form.talent_id, isOpen]);

  // Загружаем данные таланта при выборе (через Gateway — агрегированные данные)
  useEffect(() => {
    if (!form.talent_id || !isOpen) { setSelectedTalentData(null); setLoadingTalentData(false); return; }
    let cancelled = false;
    setLoadingTalentData(true);
    talents.getTalentByIdWithRelations(form.talent_id)
      .then(res => { if (!cancelled) { setSelectedTalentData(res?.data || res || null); setLoadingTalentData(false); } })
      .catch(() => { if (!cancelled) { setSelectedTalentData(null); setLoadingTalentData(false); } });
    return () => { cancelled = true; };
  }, [form.talent_id, isOpen]);

  // Автогенерация поля Name
  useEffect(() => {
    if (!isOpen) return;
    // Не перезаписываем если пользователь вручную отредактировал Name
    if (nameManuallyEdited.current) return;

    const shortName = selectedTalentData?.publicSettings?.short_name || null;
    if (!shortName) return;

    const ic = innerClientsMap.current[form.inner_client_id];
    const lang = languagesMap.current[form.translation_id];

    // Account или Social Account — только название инструмента (tool)
    const acc = accountsMap.current[form.account_id];
    const socAcc = socialAccountsMap.current[form.social_account_id];
    const tool = toolsMap.current[(socAcc || acc)?.tool_id];

    // The backend builds "short_name | client ISO | language | account tool"
    // and picks a free slug; the form only passes the selected labels
    requestIdentifiers({
      talent_id: selectedTalentData.id || form.talent_id,
      inner_client_iso: ic?.iso || undefined,
      language_iso: lang?.iso2 || undefined,
      tool_name: tool?.name || undefined
    }, { applyName: true });
  }, [selectedTalentData, form.inner_client_id, form.translation_id, form.account_id, form.social_account_id, isOpen, optionsVersion]);

  // Ask the backend for a post name and/or a free slug (debounced)
  const requestIdentifiers = (params, { applyName = false } = {}) => {
    clearTimeout(identifiersTimer.current);
    const requestId = ++identifiersRequest.current;
    identifiersTimer.current = setTimeout(async () => {
      try {
        const res = await talentPosts.generateIdentifiers({ ...params, exclude_id: isEdit ? post?.id : undefined });
        if (identifiersRequest.current !== requestId || !res?.success) return;
        const { name, slug } = res.data || {};
        setForm(prev => ({
          ...prev,
          name: applyName && name && !nameManuallyEdited.current ? name : prev.name,
          slug: slugManuallyEdited.current ? prev.slug : (slug || '')
        }));
      } catch (err) {
        console.error('Error generating post identifiers:', err);
      }
    }, 300);
  };

  const loadOptions = async (gen) => {
    setLoadingOptions(true);
    try {
      const [templatesRes, langsRes, statusesRes, accountsRes, socialAccountsRes, innerClientsRes, toolsRes, modelsRes, currenciesRes, ratesRes] = await Promise.all([
        talentTemplates.getAll({ all: true, isCommon: 1, is_draft: false }).catch(() => ({ data: [] })),
        libraries.getLanguages().catch(() => ({ data: [] })),
        statuses.getStatusesByType('talent_post', { isCommon: 1 }).catch(() => ({ data: [] })),
        accounts.getAllAccounts({ all: true, isCommon: 1 }).catch(() => ({ data: [] })),
        accounts.getAllSocialAccounts({ all: true, isCommon: 1 }).catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] })),
        libraries.getTools().catch(() => ({ data: [] })),
        talentPosts.getModels().catch(() => ({ data: [] })),
        libraries.getCurrencies().catch(() => ({ data: [] })),
        libraries.getRates().catch(() => ({ data: [] }))
      ]);
      if (requestGen.current !== gen) return;
      const tmpl = Array.isArray(templatesRes?.data) ? templatesRes.data : [];
      const langs = Array.isArray(langsRes?.data) ? langsRes.data : (Array.isArray(langsRes) ? langsRes : []);
      const statusList = Array.isArray(statusesRes?.data) ? statusesRes.data : (Array.isArray(statusesRes) ? statusesRes : []);
      const accs = Array.isArray(accountsRes?.data) ? accountsRes.data : (Array.isArray(accountsRes) ? accountsRes : []);
      const socialAccs = Array.isArray(socialAccountsRes?.data) ? socialAccountsRes.data : (Array.isArray(socialAccountsRes) ? socialAccountsRes : []);
      const innerClients = Array.isArray(innerClientsRes?.data) ? innerClientsRes.data : (Array.isArray(innerClientsRes) ? innerClientsRes : []);
      const toolsList = Array.isArray(toolsRes?.data) ? toolsRes.data : (Array.isArray(toolsRes) ? toolsRes : []);
      const modelsList = Array.isArray(modelsRes?.data) ? modelsRes.data : (Array.isArray(modelsRes) ? modelsRes : []);
      const currenciesList = Array.isArray(currenciesRes?.data) ? currenciesRes.data : (Array.isArray(currenciesRes) ? currenciesRes : []);
      const ratesList = Array.isArray(ratesRes?.data) ? ratesRes.data : (Array.isArray(ratesRes) ? ratesRes : []);
      const draftStatus = statusList.find(s => String(s.name || '').toLowerCase() === 'draft') || null;
      const englishLang = langs.find(l => {
        const label = String(l.name || l.title || '').toLowerCase();
        const iso = String(l.iso2 || l.iso_639_1 || '').toLowerCase();
        return label === 'english' || iso === 'en';
      }) || null;
      const defaultModel = modelsList.find(m => m.id === 'google/gemini-2.0-flash-001') || null;
      setTemplateOptions(tmpl.map(t => ({ value: String(t.id), label: t.name, _raw: t })));
      languagesMap.current = Object.fromEntries(langs.map(l => [l.id, l]));
      setLanguageOptions(langs.map(l => ({ value: String(l.id), label: l.name || l.title })));
      setStatusOptions(statusList.map(s => ({ value: String(s.id), label: s.name })));
      accountsMap.current = Object.fromEntries(accs.map(a => [a.id, a]));
      setAccountOptions(accs.map(a => ({ value: String(a.id), label: a.name || `Account #${a.id}` })));
      socialAccountsMap.current = Object.fromEntries(socialAccs.map(a => [a.id, a]));
      setSocialAccountOptions(socialAccs.map(a => ({ value: String(a.id), label: a.username || a.handle || a.name || `Social #${a.id}` })));
      innerClientsMap.current = Object.fromEntries(innerClients.map(c => [c.id, c]));
      setInnerClientOptions(innerClients.map(c => ({ value: String(c.id), label: c.name })));
      toolsMap.current = Object.fromEntries(toolsList.map(t => [t.id, t]));
      setModelOptions(modelsList.map(m => ({ value: m.id, label: m.name || m.id })));
      if (defaultModel) setSelectedModel(defaultModel.id);
      setCurrencyOptions(currenciesList.map(c => ({ value: String(c.id), label: `${c.name} (${c.symbol})` })));
      setRateOptions(ratesList.map(r => ({ value: r.id, label: `${r.name} (${r.hours}h, ${r.value})` })));
      if (!post?.id) {
        setForm((prev) => {
          const updates = {};
          if (prev.status_id == null && draftStatus) updates.status_id = String(draftStatus.id);
          if (prev.translation_id == null && englishLang) updates.translation_id = String(englishLang.id);
          return Object.keys(updates).length ? { ...prev, ...updates } : prev;
        });
      }
      setOptionsVersion(v => v + 1);
      if (!fixedTalentId) loadTalentOptions(gen);
    } catch (err) { console.error('Failed to load options:', err); }
    finally { if (requestGen.current === gen) setLoadingOptions(false); }
  };

  const loadTalentOptions = async (gen) => {
    try {
      const savedTalentId = isEdit ? (post?.talent_id || null) : (isDuplicate ? (duplicateFrom?.talent_id || null) : null);
      const res = await talents.getTalents({ all: true, isCommon: 1, is_active: true });
      if (requestGen.current !== gen) return;
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      const toOption = t => {
        const fullName = [t.first_name, t.last_name].filter(Boolean).join(' ') || `Talent #${t.id}`;
        const shortName = t.publicSettings?.short_name;
        const slug = t.publicSettings?.slug;
        const extra = [shortName, slug].filter(Boolean).join(' / ');
        return { value: String(t.id), label: extra ? `${fullName} (${extra})` : fullName, _short_name: shortName, _slug: slug };
      };
      const options = list.map(toOption);
      if (savedTalentId && !options.find(o => o.value === String(savedTalentId))) {
        try {
          const saved = await talents.getTalentById(savedTalentId);
          if (requestGen.current !== gen) return;
          const t = saved?.data || saved;
          if (t?.id) options.unshift(toOption(t));
        } catch (_) {}
      }
      setTalentOptions(options);
    } catch (err) { console.error('Failed to load talents:', err); }
  };

  const loadTermsFromApi = async (postId, isCancelled = () => false) => {
    setLoadingTerms(true);
    try {
      const res = await talentPosts.getTerms({ postId });
      if (isCancelled()) return;
      const data = res?.data;
      if (!data) return;
      setProfessionTerms(data.professions || []);
      setResponsibilityTerms(data.responsibilities || []);
      const pChoices = {};
      for (const p of (data.professions || [])) {
        if (p.saved_is_similar !== null) {
          pChoices[p.talent_profession_id] = { is_similar: p.saved_is_similar, similar_term_id: p.saved_similar_term_id, is_primary: p.is_primary ?? false };
        } else {
          pChoices[p.talent_profession_id] = { is_similar: p.recommended === 'similar', similar_term_id: p.recommended === 'similar' ? p.recommended_similar_term_id : null, is_primary: p.is_primary ?? false };
        }
      }
      setProfChoices(pChoices);
      const rChoices = {};
      for (const r of (data.responsibilities || [])) {
        if (r.saved_is_similar !== null) {
          rChoices[r.responsibility_id] = { is_similar: r.saved_is_similar, term_id: r.saved_term_id };
        } else {
          rChoices[r.responsibility_id] = { is_similar: r.recommended === 'similar', term_id: r.recommended === 'similar' ? r.recommended_term_id : r.main_term_id };
        }
      }
      setRespChoices(rChoices);
      // Загружаем сохранённые цены из поста (edit/duplicate)
      const sourcePost = isEdit ? post : duplicateFrom;
      // Автозаполнение из profession.rates (базовые цены)
      const autoPrices = {};
      for (const p of (data.professions || [])) {
        for (const r of (p.rates || [])) {
          autoPrices[`${p.profession_id}_${r.id}`] = {
            price: r.profession_rate?.price ?? '',
            currency_id: strOrNull(p.currency_id)
          };
        }
      }
      if (sourcePost?.postPrices?.length) {
        const savedPrices = { ...autoPrices };
        const savedCustomIds = new Set();
        for (const sp of sourcePost.postPrices) {
          savedPrices[`${sp.profession_id}_${sp.rate_id}`] = { id: isEdit ? sp.id : undefined, price: sp.price ?? '', currency_id: strOrNull(sp.currency_id) };
          savedCustomIds.add(sp.profession_id);
        }
        setPriceChoices(savedPrices);
        setCustomProfIds(savedCustomIds);
      } else {
        setPriceChoices(autoPrices);
        setCustomProfIds(new Set());
      }
    } catch (err) { if (!isCancelled()) console.error('Failed to load terms from API:', err); }
    finally { if (!isCancelled()) setLoadingTerms(false); }
  };

  const loadTermsFromTalent = async (talentId, isCancelled = () => false) => {
    setLoadingTerms(true);
    try {
      const res = await talentPosts.getTerms({ talentId });
      if (isCancelled()) return;
      const data = res?.data;
      if (!data) return;
      setProfessionTerms(data.professions || []);
      setResponsibilityTerms(data.responsibilities || []);
      const pChoices = {};
      for (const p of (data.professions || [])) {
        pChoices[p.talent_profession_id] = { is_similar: p.recommended === 'similar', similar_term_id: p.recommended === 'similar' ? p.recommended_similar_term_id : null, is_primary: p.is_primary ?? false };
      }
      setProfChoices(pChoices);
      const rChoices = {};
      for (const r of (data.responsibilities || [])) {
        rChoices[r.responsibility_id] = { is_similar: r.recommended === 'similar', term_id: r.recommended === 'similar' ? r.recommended_term_id : r.main_term_id };
      }
      setRespChoices(rChoices);
      // Автозаполнение цен из profession.rates
      const autoPrices = {};
      for (const p of (data.professions || [])) {
        for (const r of (p.rates || [])) {
          autoPrices[`${p.profession_id}_${r.id}`] = {
            price: r.profession_rate?.price ?? '',
            currency_id: strOrNull(p.currency_id)
          };
        }
      }
      setPriceChoices(autoPrices);
      setCustomProfIds(new Set());
    } catch (err) { if (!isCancelled()) console.error('Failed to load talent terms:', err); }
    finally { if (!isCancelled()) setLoadingTerms(false); }
  };

  const handleTemplateChange = (val) => {
    setForm(prev => {
      if (!val) return { ...prev, talent_template_id: null };
      const opt = templateOptions.find(o => o.value === val);
      const tmpl = opt?._raw || {};
      const nextInnerId = prev.inner_client_id || tmpl.inner_client_id || null;
      const ic = nextInnerId ? innerClientsMap.current[nextInnerId] : null;
      const nextIso = (ic?.iso || tmpl.inner_client_iso || prev.inner_client_iso || '').trim();
      return {
        ...prev, talent_template_id: val,
        blocks_config: { ...DEFAULT_BLOCKS, ...(tmpl.blocks_config || {}) },
        inner_client_id: nextInnerId,
        inner_client_iso: nextIso
      };
    });
  };

  const resetNameSlugFlags = () => {
    nameManuallyEdited.current = false;
    slugManuallyEdited.current = false;
    // A response for the previous selection must not land in the form
    clearTimeout(identifiersTimer.current);
    identifiersRequest.current++;
  };

  // Only a new talent starts name and slug over: the name is built from the
  // talent's short_name. Client, language and account changes keep whatever
  // was typed by hand and only refresh the fields that are still auto-generated.
  const handleTalentChange = (val) => {
    resetNameSlugFlags();
    setForm(prev => ({ ...prev, talent_id: val, name: '', slug: '' }));
    setSlugStatus(null);
    setSlugExisting(null);
  };

  const handleInnerClientChange = (val) => {
    const ic = val ? innerClientsMap.current[val] : null;
    setForm(prev => ({ ...prev, inner_client_id: val, inner_client_iso: ic?.iso ? String(ic.iso) : '' }));
  };

  const handleTranslationChange = (val) => {
    setForm(prev => ({ ...prev, translation_id: val }));
  };

  const handleAccountChange = (val) => {
    setForm(prev => ({ ...prev, account_id: val, social_account_id: val ? null : prev.social_account_id }));
  };

  const handleSocialAccountChange = (val) => {
    setForm(prev => ({ ...prev, social_account_id: val, account_id: val ? null : prev.account_id }));
  };

  const slugCheckTimer = useRef(null);

  const checkSlugUniqueness = async (slug) => {
    if (!slug.trim()) { setSlugStatus(null); setSlugExisting(null); return; }
    setCheckingSlug(true);
    try {
      const res = await talentPosts.checkSlug(slug.trim(), isEdit ? post.id : null);
      setSlugStatus(res?.available ? 'available' : 'taken');
      setSlugExisting(res?.existing || null);
    } catch (err) {
      // 400: nothing is left of the value once the backend cleans it up
      setSlugStatus(err?.response?.status === 400 ? 'invalid' : null);
      setSlugExisting(null);
    }
    finally { setCheckingSlug(false); }
  };

  // The backend owns the slug format: on blur the field shows the slug as it will be stored
  const handleSlugBlur = async () => {
    const typed = form.slug?.trim();
    if (!typed) return;
    try {
      const res = await talentPosts.checkSlug(typed, isEdit ? post.id : null);
      if (res?.slug && res.slug !== form.slug) setForm(prev => (prev.slug === form.slug ? { ...prev, slug: res.slug } : prev));
    } catch (err) { /* the debounced check reports the status */ }
  };

  const handleCheckSlug = async () => {
    if (!form.slug.trim()) return;
    clearTimeout(slugCheckTimer.current);
    await checkSlugUniqueness(form.slug);
  };

  // Автопроверка slug при каждом его изменении (с debounce 600ms)
  useEffect(() => {
    if (!isOpen) return;
    const slug = form.slug?.trim();
    setSlugStatus(null); setSlugExisting(null);
    clearTimeout(slugCheckTimer.current);
    if (!slug) return;
    slugCheckTimer.current = setTimeout(() => checkSlugUniqueness(slug), 600);
    return () => clearTimeout(slugCheckTimer.current);
  }, [form.slug, isOpen]);

  const numOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const resolveInnerClientIso = () => {
    const fromForm = (form.inner_client_iso && String(form.inner_client_iso).trim()) || '';
    if (fromForm) return fromForm;
    const ic = form.inner_client_id ? innerClientsMap.current[form.inner_client_id] : null;
    if (ic?.iso) return String(ic.iso).trim();
    return null;
  };

  const buildPayload = () => ({
    talent_id: numOrNull(form.talent_id),
    talent_template_id: numOrNull(form.talent_template_id),
    name: form.name.trim(), slug: form.slug.trim() || null,
    status_id: numOrNull(form.status_id),
    translation_id: numOrNull(form.translation_id),
    inner_client_id: numOrNull(form.inner_client_id),
    inner_client_iso: resolveInnerClientIso(),
    account_id: numOrNull(form.account_id),
    social_account_id: numOrNull(form.social_account_id),
    planned_date: form.planned_date || null, end_date: form.end_date || null,
    discount: form.discount !== '' && !Number.isNaN(parseFloat(form.discount)) ? parseFloat(form.discount) : null,
    blocks_config: form.blocks_config, full_post: form.full_post || null
  });

  const saveTermsIfNeeded = async (postId, isNew = false) => {
    const hasProfChoices = Object.keys(profChoices).length > 0;
    const hasRespChoices = Object.keys(respChoices).length > 0;
    if (postId && (hasProfChoices || hasRespChoices)) {
      await talentPosts.saveTerms(postId, {
        professions: professionTerms.filter(p => profChoices[p.talent_profession_id]).map(p => { const c = profChoices[p.talent_profession_id]; return { id: p.saved_post_profession_id || undefined, talent_profession_id: p.talent_profession_id, profession_id: p.profession_id, is_similar: c.is_similar, similar_term_id: c.similar_term_id || null, is_primary: typeof c.is_primary === 'boolean' ? c.is_primary : p.is_primary }; }),
        responsibilities: responsibilityTerms.filter(r => respChoices[r.responsibility_id]).map(r => { const c = respChoices[r.responsibility_id]; return { id: r.saved_post_responsibility_id || undefined, responsibility_id: r.responsibility_id, is_similar: c.is_similar, term_id: c.term_id || null }; })
      }, isNew);
    }
  };

  const savePricesIfNeeded = async (postId, isNew = false) => {
    if (!postId) return;
    if (!pricesChanged.current && customProfIds.size === 0) return;
    const prices = professionTerms
      .filter(p => customProfIds.has(p.profession_id))
      .flatMap(p =>
        (p.rates || []).map(r => {
          const entry = priceChoices[`${p.profession_id}_${r.id}`] || {};
          return {
            id: entry.id || undefined,
            profession_id: p.profession_id,
            rate_id: r.id,
            price: entry.price || null,
            currency_id: entry.currency_id ? Number(entry.currency_id) : null
          };
        })
      );
    if (prices.length) {
      await talentPosts.savePrices(postId, { prices }, isNew);
    }
  };

  const handleGenerate = async (feedbackText = null) => {
    if (!form.talent_template_id || !form.talent_id) return;
    setGenerating(true);
    try {
      // Build revisions list with feedback
      let revisionsToSend = revisions;
      if (feedbackText && revisions.length > 0) {
        revisionsToSend = [
          ...revisions.slice(0, -1),
          { ...revisions[revisions.length - 1], feedback: feedbackText }
        ];
      }

      // Resolve profession names from UI state
      const resolvedProfessions = professionTerms.map(p => {
        const choice = profChoices[p.talent_profession_id];
        if (choice?.is_similar && choice?.similar_term_id) {
          const sim = (p.similar_terms || []).find(t => t.id === choice.similar_term_id);
          return { profession_name: sim?.name || p.profession_name || '' };
        }
        return { profession_name: p.profession_name || '' };
      }).filter(p => p.profession_name);

      // Resolve responsibility term names from UI state
      const resolvedResponsibilities = responsibilityTerms.map(r => {
        const choice = respChoices[r.responsibility_id];
        const termId = choice?.term_id ?? r.main_term_id;
        if (termId === r.main_term_id) {
          return { term_value: r.main_term_name || r.responsibility_name || '' };
        }
        const sim = (r.similar_terms || []).find(t => t.id === termId);
        return { term_value: sim?.name || r.main_term_name || r.responsibility_name || '' };
      }).filter(r => r.term_value);

      // Build talent object from selectedTalentData
      const talentObj = selectedTalentData ? {
        first_name: selectedTalentData.first_name || '',
        last_name: selectedTalentData.last_name || '',
        cv: selectedTalentData.cv || [],
        media: selectedTalentData.media || [],
        tools: selectedTalentData.tools || [],
        languages: selectedTalentData.languages || [],
      } : { first_name: '', last_name: '', cv: [], media: [], tools: [], languages: [] };

      const innerClientObj = form.inner_client_id ? (innerClientsMap.current[form.inner_client_id] || null) : null;
      const langObj = form.translation_id ? (languagesMap.current[form.translation_id] || null) : null;

      // Get template data for prompt resolution
      const templateOpt = templateOptions.find(o => o.value === form.talent_template_id);
      const templateRaw = templateOpt?._raw || {};

      const payload = {
        talent: talentObj,
        inner_client: innerClientObj,
        translation: langObj ? { id: langObj.id, name: langObj.name || langObj.title } : null,
        blocks_config: form.blocks_config,
        professions: resolvedProfessions,
        responsibilities: resolvedResponsibilities,
        prompt_id: templateRaw.prompt_id || null,
        tool_id: templateRaw.tool_id || null,
        revisions: revisionsToSend,
        model: selectedModel || null
      };

      const res = await talentPosts.generate(payload);
      const generated = res?.data || res;
      const draft = generated?.full_post || '';

      const newRevisions = [
        ...(feedbackText && revisions.length > 0
          ? [...revisions.slice(0, -1), { ...revisions[revisions.length - 1], feedback: feedbackText }]
          : revisions),
        { draft, feedback: null }
      ];
      setRevisions(newRevisions);
      setGenFeedback('');
      setShowGenFeedback(false);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleAcceptDraft = () => {
    if (!revisions.length) return;
    const draft = revisions[revisions.length - 1].draft;
    setForm(prev => ({ ...prev, full_post: draft }));
    setRevisions([]);
    setGenFeedback('');
    setShowGenFeedback(false);
    toast.success('Draft accepted');
  };

  const handleDiscardDraft = () => {
    setRevisions([]);
    setGenFeedback('');
    setShowGenFeedback(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.talent_template_id) { scrollToRef(refTemplate); toast.error('Template is required'); return; }
    if (!form.name.trim()) { scrollToRef(refName); toast.error('Name is required'); return; }
    if (!form.slug.trim()) { scrollToRef(refSlug); toast.error('Slug is required'); return; }
    if (slugStatus === 'taken') { scrollToRef(refSlug); toast.error('This slug is already taken, please choose another'); return; }
    if (slugStatus === 'invalid') { scrollToRef(refSlug); toast.error('Slug must contain Latin letters or digits'); return; }
    if (checkingSlug) { scrollToRef(refSlug); toast.error('Please wait while slug is being checked'); return; }
    if (!form.talent_id) { scrollToRef(refTalent); toast.error('Talent is required'); return; }
    if (!form.status_id) { scrollToRef(refStatus); toast.error('Status is required'); return; }
    if (!form.inner_client_id) { scrollToRef(refInnerClient); toast.error('Inner Client is required'); return; }
    if (!form.translation_id) { scrollToRef(refTranslation); toast.error('Translation is required'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let postId;
      if (isEdit) {
        postId = post.id;
        await talentPosts.update(postId, payload);
        toast.success('Post updated');
      } else {
        const res = await talentPosts.create(payload);
        const saved = res?.data || res;
        postId = saved?.id;
        toast.success('Post created');
      }
      await saveTermsIfNeeded(postId, !isEdit);
      await savePricesIfNeeded(postId, !isEdit);
      onSaved();
      onClose();
    } catch (err) {
      const details = err.response?.data?.details;
      if (details?.length) {
        details.forEach(d => toast.error(d));
      } else {
        toast.error(err.response?.data?.error || err.message || 'Failed to save');
      }
    } finally { setSaving(false); }
  };

  const activeBlocks = Object.values(form.blocks_config).filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose} ariaLabel={isEdit ? 'Edit talent post' : 'Create talent post'}>
      <ModalFrame size="xl" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        {/* ── Header ─────────────────────────────────────────────── */}
        <ModalFrame.Header className="items-center px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
              <FileText size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary leading-tight">
                {isEdit ? 'Edit Post' : isDuplicate ? 'Duplicate Post' : 'New Talent Post'}
              </h2>
              <p className="truncate text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                Configure post settings & content blocks
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-light-text-tertiary hover:bg-light-bg-secondary dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        {/* ── Body ───────────────────────────────────────────────── */}
        <ModalFrame.Body className="p-0">
          <div className="space-y-5 p-4 sm:p-6">

            {/* ── Template (required, primary) ──────────────────── */}
            <div ref={refTemplate}>
              <Divider icon={LayoutTemplate} label="Template" color="text-teal-500 dark:text-teal-400" />
              <div className="mt-3">
                <label className={LABEL}>
                  Template <span className="text-red-400 normal-case font-normal">*</span>
                </label>
                <Autocomplete
                  clearable
                  fullWidth
                  options={templateOptions}
                  value={form.talent_template_id}
                  onChange={handleTemplateChange}
                  placeholder={loadingOptions ? 'Loading…' : 'Select template...'}
                  disabled={loadingOptions}
                />
                {!form.talent_template_id && (
                  <p className="mt-1.5 text-[11px] text-amber-500 dark:text-amber-400">
                    Template defines the prompt, platform and content blocks for this post.
                  </p>
                )}
              </div>
            </div>

            {/* ── Identity ──────────────────────────────────────── */}
            <div className="space-y-3">
              {!fixedTalentId && (
                <div ref={refTalent}>
                  <label className={LABEL}>Talent <span className="text-red-400 normal-case font-normal">*</span></label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={talentOptions}
                    value={form.talent_id}
                    onChange={handleTalentChange}
                    placeholder={loadingOptions ? 'Loading…' : 'Select talent...'}
                    disabled={loadingOptions}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_160px]">
                <div ref={refName}>
                  <TextField
                    label="Name"
                    required
                    size="small"
                    fullWidth
                    value={form.name}
                    disabled={loadingTalentData && !nameManuallyEdited.current}
                    onChange={e => {
                      nameManuallyEdited.current = true;
                      const name = e.target.value;
                      setForm(prev => ({ ...prev, name }));
                      // The slug follows a hand-typed name until the slug itself is edited
                      if (!slugManuallyEdited.current) {
                        if (name.trim()) requestIdentifiers({ name });
                        else {
                          clearTimeout(identifiersTimer.current);
                          identifiersRequest.current++;
                          setForm(prev => ({ ...prev, slug: '' }));
                        }
                      }
                    }}
                    placeholder={loadingTalentData && !nameManuallyEdited.current ? 'Generating…' : 'e.g. LinkedIn April 2026'}
                  />
                </div>
                <div ref={refStatus}>
                  <label className={LABEL}>Status <span className="text-red-400 normal-case font-normal">*</span></label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={statusOptions}
                    value={form.status_id}
                    onChange={val => setForm(prev => ({ ...prev, status_id: val || null }))}
                    placeholder={loadingOptions ? 'Loading…' : 'Status...'}
                    disabled={loadingOptions}
                  />
                </div>
              </div>

              {/* Slug */}
              <div ref={refSlug}>
                <div className="flex gap-2 items-end">
                  <TextField
                    label="Slug"
                    required
                    size="small"
                    fullWidth
                    value={form.slug}
                    disabled={loadingTalentData && !slugManuallyEdited.current}
                    error={slugStatus === 'taken' || slugStatus === 'invalid'}
                    onChange={e => {
                      slugManuallyEdited.current = true;
                      setForm(prev => ({ ...prev, slug: e.target.value }));
                    }}
                    onBlur={handleSlugBlur}
                    placeholder={loadingTalentData && !slugManuallyEdited.current ? 'Generating…' : 'e.g. linkedin-april-2026'}
                  />
                  <button
                    type="button"
                    onClick={handleCheckSlug}
                    disabled={checkingSlug || !form.slug.trim()}
                    className="shrink-0 px-3 py-2 text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary disabled:opacity-40 transition-colors"
                  >
                    {checkingSlug ? '…' : 'Check'}
                  </button>
                  {slugStatus === 'available' && <CheckCircle size={18} className="shrink-0 text-emerald-500" />}
                  {(slugStatus === 'taken' || slugStatus === 'invalid') && <XCircle size={18} className="shrink-0 text-red-500" />}
                </div>
                {slugStatus === 'invalid' && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">Use Latin letters, digits and hyphens</p>
                )}
                {slugStatus === 'taken' && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    This slug is already taken.{' '}
                    {slugExisting && (
                      <>
                        Used by post:{' '}
                        <a
                          href={`/talent-posts/${slugExisting.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-red-700 dark:hover:text-red-300"
                        >
                          {slugExisting.name}
                        </a>
                      </>
                    )}
                  </p>
                )}
                {slugStatus === 'available' && (
                  <p className="mt-1 text-xs text-emerald-500 dark:text-emerald-400">Slug is available</p>
                )}
              </div>
            </div>

            {/* ── Publication Target ────────────────────────────── */}
            <div>
              <Divider icon={Target} label="Publication Target" color="text-blue-500 dark:text-blue-400" />
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div ref={refInnerClient}>
                    <label className={LABEL}>Inner Client <span className="text-red-400 normal-case font-normal">*</span></label>
                    <Autocomplete
                      clearable
                      fullWidth
                      options={innerClientOptions}
                      value={form.inner_client_id}
                      onChange={handleInnerClientChange}
                      placeholder={loadingOptions ? 'Loading…' : 'Select client...'}
                      disabled={loadingOptions}
                    />
                  </div>
                  <div ref={refTranslation}>
                    <label className={LABEL}>Translation <span className="text-red-400 normal-case font-normal">*</span></label>
                    <Autocomplete clearable fullWidth options={languageOptions} value={form.translation_id} onChange={handleTranslationChange} placeholder={loadingOptions ? 'Loading…' : 'Generation language...'} disabled={loadingOptions} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={LABEL}>Account</label>
                    <Autocomplete clearable fullWidth options={accountOptions} value={form.account_id} onChange={handleAccountChange} placeholder={loadingOptions ? 'Loading…' : 'Select account...'} disabled={loadingOptions || !!form.social_account_id} />
                  </div>
                  <div>
                    <label className={LABEL}>Social Account</label>
                    <Autocomplete clearable fullWidth options={socialAccountOptions} value={form.social_account_id} onChange={handleSocialAccountChange} placeholder={loadingOptions ? 'Loading…' : 'Select social...'} disabled={loadingOptions || !!form.account_id} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Terms ─────────────────────────────────────────── */}
            {form.talent_id && (
              <div>
                <Divider icon={Cpu} label="AI Terms" color="text-violet-500 dark:text-violet-400" />
                <div className="space-y-2 mt-3">

                  <TermSection
                    title="Professions"
                    count={professionTerms.length}
                    loading={loadingTerms}
                    open={showProfessions}
                    onToggle={() => setShowProfessions(v => !v)}
                    empty={professionTerms.length === 0}
                  >
                    {professionTerms.map(p => {
                      const choice = profChoices[p.talent_profession_id];
                      const isMainSelected = choice ? !choice.is_similar : !(p.saved_is_similar ?? false);
                      const selectedSimilarId = choice ? choice.similar_term_id : (p.saved_similar_term_id ?? null);
                      const variants = [
                        { id: p.main_term_id, label: p.profession_name, isSelected: isMainSelected, isRecommended: (p.recommended ?? 'main') === 'main', usedByOthers: !!p.main_used_by_others, isSimilar: false },
                        ...(p.similar_terms || []).map(t => ({ id: t.id, label: t.name, isSelected: !isMainSelected && selectedSimilarId === t.id, isRecommended: p.recommended === 'similar' && t.id === p.recommended_similar_term_id, usedByOthers: !!t.used_by_others, isSimilar: true }))
                      ];
                      const isPrimary = choice ? (typeof choice.is_primary === 'boolean' ? choice.is_primary : p.is_primary) : p.is_primary;
                      return (
                        <div key={p.talent_profession_id} className="px-4 py-2.5">
                          <div className="inline-flex items-center mb-1">
                            <Checkbox
                              checked={!!isPrimary}
                              onChange={() => setProfChoices(prev => {
                                const newIsPrimary = !isPrimary;
                                const updated = newIsPrimary
                                  ? Object.fromEntries(Object.entries(prev).map(([k, c]) => [k, { ...c, is_primary: false }]))
                                  : { ...prev };
                                const existing = updated[p.talent_profession_id] || { is_similar: !(p.saved_is_similar ?? false), similar_term_id: p.saved_similar_term_id ?? null };
                                return { ...updated, [p.talent_profession_id]: { ...existing, is_primary: newIsPrimary } };
                              })}
                              size="small"
                              color="primary"
                              label={(
                                <span className={`text-[10px] font-semibold uppercase tracking-wide ${isPrimary ? 'text-blue-500' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                                  Primary
                                </span>
                              )}
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {variants.map(v => (
                              <button key={v.id ?? 'main'} type="button"
                                onClick={() => setProfChoices(prev => ({ ...prev, [p.talent_profession_id]: { is_similar: v.isSimilar, similar_term_id: v.isSimilar ? v.id : null } }))}
                                title={v.usedByOthers ? 'Used in another post' : undefined}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-all duration-150 ${
                                  v.isSelected
                                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                                    : 'border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:border-blue-400 dark:hover:border-blue-500'
                                }`}
                              >
                                {v.isRecommended && <span className={v.isSelected ? 'text-yellow-200' : 'text-amber-400'}>★</span>}
                                <span>{v.label}</span>
                                {v.usedByOthers && <AlertCircle size={10} className={v.isSelected ? 'text-yellow-200' : 'text-orange-400'} />}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </TermSection>

                  <TermSection
                    title="Responsibilities"
                    count={responsibilityTerms.length}
                    loading={loadingTerms}
                    open={showResponsibilities}
                    onToggle={() => setShowResponsibilities(v => !v)}
                    empty={responsibilityTerms.length === 0}
                  >
                    {responsibilityTerms.map(r => {
                      const choice = respChoices[r.responsibility_id];
                      const selectedTermId = choice?.term_id ?? r.saved_term_id ?? r.main_term_id;
                      const variants = [
                        { id: r.main_term_id, label: r.main_term_name || r.responsibility_name || `#${r.responsibility_id}`, isRecommended: (r.recommended ?? 'main') === 'main', usedByOthers: !!r.main_used_by_others },
                        ...(r.similar_terms || []).map(t => ({ id: t.id, label: t.name, isRecommended: r.recommended === 'similar' && t.id === r.recommended_term_id, usedByOthers: !!t.used_by_others }))
                      ];
                      return (
                        <div key={r.responsibility_id} className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1.5">
                            {variants.map(v => {
                              const isSelected = v.id === selectedTermId;
                              return (
                                <button key={v.id ?? 'main'} type="button"
                                  onClick={() => setRespChoices(prev => ({ ...prev, [r.responsibility_id]: { is_similar: v.id !== r.main_term_id, term_id: v.id } }))}
                                  title={v.usedByOthers ? 'Used in another post' : undefined}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-all duration-150 ${
                                    isSelected
                                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                                      : 'border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:border-blue-400 dark:hover:border-blue-500'
                                  }`}
                                >
                                  {v.isRecommended && <span className={isSelected ? 'text-yellow-200' : 'text-amber-400'}>★</span>}
                                  <span>{v.label}</span>
                                  {v.usedByOthers && <AlertCircle size={10} className={isSelected ? 'text-yellow-200' : 'text-orange-400'} />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </TermSection>

                </div>
              </div>
            )}

            {/* ── Prices ────────────────────────────────────────── */}
            {form.talent_id && professionTerms.some(p => (p.rates || []).length > 0) && (
              <div>
                <Divider icon={null} label="Prices" color="text-green-600 dark:text-green-400" />
                <div className="space-y-3 mt-3">
                  {professionTerms.filter(p => (p.rates || []).length > 0).map(p => {
                    const profId = p.profession_id;
                    const isEditingThis = !!pricesEditByProf[profId];
                    const isCustomProf = customProfIds.has(profId);
                    const priceChoice = profChoices[p.talent_profession_id];
                    const isPriceBlockPrimary = priceChoice ? (typeof priceChoice.is_primary === 'boolean' ? priceChoice.is_primary : p.is_primary) : p.is_primary;
                    return (
                      <div key={profId} className="rounded-xl border border-light-border-primary dark:border-dark-border-primary sm:overflow-hidden">
                        <div className="flex flex-col items-stretch gap-2 bg-light-bg-secondary px-4 py-2 dark:bg-dark-bg-tertiary sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="min-w-0 break-words text-xs font-semibold uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">
                              {p.profession_name}
                            </span>
                            {isPriceBlockPrimary && <span className="text-[10px] font-semibold text-blue-500">Primary</span>}
                            {isCustomProf && <span className="text-[10px] font-semibold text-amber-500 dark:text-amber-400">Custom</span>}
                          </div>
                          {isEditingThis ? (
                            <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
                              <span className="text-[10px] uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary">Currency</span>
                              <div className="w-full min-w-0 sm:w-36">
                                <Autocomplete
                                  clearable
                                  fullWidth
                                  options={currencyOptions}
                                  value={priceChoices[`${profId}_${(p.rates[0] || {}).id}`]?.currency_id ?? null}
                                  onChange={val => {
                                    setPriceChoices(prev => {
                                      const next = { ...prev };
                                      for (const r of (p.rates || [])) {
                                        const key = `${profId}_${r.id}`;
                                        next[key] = { ...(next[key] || {}), currency_id: val || null };
                                      }
                                      return next;
                                    });
                                  }}
                                  placeholder="Currency..."
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const snap = priceSnapshot.current[profId];
                                  if (snap) setPriceChoices(prev => ({ ...prev, ...snap }));
                                  setPricesEditByProf(prev => ({ ...prev, [profId]: false }));
                                  delete priceSnapshot.current[profId];
                                }}
                                className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary rounded hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  pricesChanged.current = true;
                                  setCustomProfIds(prev => new Set([...prev, profId]));
                                  setPricesEditByProf(prev => ({ ...prev, [profId]: false }));
                                  delete priceSnapshot.current[profId];
                                }}
                                className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                              >
                                Done
                              </button>
                            </div>
                          ) : (
                            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
                              {isCustomProf && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Сбросить к ценам из профессии
                                    const autoPrices = {};
                                    for (const r of (p.rates || [])) {
                                      autoPrices[`${profId}_${r.id}`] = { price: r.profession_rate?.price ?? '', currency_id: strOrNull(p.currency_id) };
                                    }
                                    setPriceChoices(prev => ({ ...prev, ...autoPrices }));
                                    setCustomProfIds(prev => { const next = new Set(prev); next.delete(profId); return next; });
                                    pricesChanged.current = true;
                                  }}
                                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary rounded hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                                >
                                  <RotateCcw size={9} />
                                  Reset to Profession
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  // Снапшот текущих цен (или из profession_rate если не кастом)
                                  const snap = {};
                                  for (const r of (p.rates || [])) {
                                    const key = `${profId}_${r.id}`;
                                    snap[key] = { ...(priceChoices[key] || { price: r.profession_rate?.price ?? '', currency_id: strOrNull(p.currency_id) }) };
                                  }
                                  priceSnapshot.current[profId] = snap;
                                  // Если не кастом — подставить цены из профессии для редактирования
                                  if (!isCustomProf) {
                                    setPriceChoices(prev => ({ ...prev, ...snap }));
                                  }
                                  setPricesEditByProf(prev => ({ ...prev, [profId]: true }));
                                }}
                                className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                              >
                                <Pencil size={9} />
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
                          {(p.rates || []).map(r => {
                            const key = `${profId}_${r.id}`;
                            const entry = priceChoices[key] || {};
                            const profPrice = r.profession_rate?.price ?? null;
                            return (
                              <div key={r.id} className="px-4 py-2.5 flex items-center gap-3">
                                <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary w-36 shrink-0">
                                  {r.name} ({r.hours}h)
                                </span>
                                {isEditingThis ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    step="0.01"
                                    min="0"
                                    value={entry.price ?? ''}
                                    onChange={e => setPriceChoices(prev => ({ ...prev, [key]: { ...(prev[key] || {}), price: e.target.value } }))}
                                    placeholder="0.00"
                                    sx={{ root: 'max-w-[140px]' }}
                                  />
                                ) : (
                                  <span className={`text-sm ${isCustomProf ? 'text-light-text-primary dark:text-dark-text-primary' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                                    {isCustomProf
                                      ? (entry.price != null && entry.price !== '' ? entry.price : <span className="opacity-50">—</span>)
                                      : (profPrice != null ? profPrice : <span className="opacity-50">—</span>)
                                    }
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Planning ──────────────────────────────────────── */}
            <div>
              <Divider icon={Calendar} label="Planning" color="text-amber-500 dark:text-amber-400" />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <DatePicker
                    label="Planned Date"
                    size="small"
                    fullWidth
                    value={form.planned_date ? new Date(form.planned_date) : null}
                    onChange={d => setForm(prev => ({ ...prev, planned_date: d ? d.toISOString().slice(0, 10) : '' }))}
                  />
                </div>
                <div>
                  <DatePicker
                    label="End Date"
                    size="small"
                    fullWidth
                    value={form.end_date ? new Date(form.end_date) : null}
                    onChange={d => setForm(prev => ({ ...prev, end_date: d ? d.toISOString().slice(0, 10) : '' }))}
                  />
                </div>
                <div>
                  <TextField
                    label="Discount (%)"
                    type="number"
                    size="small"
                    fullWidth
                    value={form.discount}
                    onChange={e => setForm(prev => ({ ...prev, discount: e.target.value }))}
                    placeholder="e.g. 10 or -10"
                    step="0.01"
                  />
                  {form.discount !== '' && !isNaN(parseFloat(form.discount)) && (
                    <p className="mt-1 text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">
                      {parseFloat(form.discount) >= 0
                        ? `Price will decrease by ${parseFloat(form.discount)}%`
                        : `Price will increase by ${Math.abs(parseFloat(form.discount))}%`}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Content Blocks ────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={LABEL + ' mb-0'}>Content Blocks</label>
                <span className="text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary">{activeBlocks} / {Object.keys(BLOCK_META).length} active</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(BLOCK_META).map(([key, { label, icon }]) => (
                  <BlockChip key={key} label={label} icon={icon} active={!!form.blocks_config[key]} onClick={() => setForm(prev => ({ ...prev, blocks_config: { ...prev.blocks_config, [key]: !prev.blocks_config[key] } }))} />
                ))}
              </div>
            </div>

            {/* ── AI Generation ─────────────────────────────────── */}
            {form.talent_template_id && form.talent_id && (
              <div className="space-y-3">
                <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                      <span className="font-medium">AI Generation</span>
                      {!form.inner_client_id && <span className="ml-2 text-amber-500">— select Inner Client for best results</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleGenerate(null)}
                      disabled={generating}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-all whitespace-nowrap shadow-sm"
                    >
                      <Wand2 size={14} />
                      {generating ? 'Generating…' : revisions.length > 0 ? 'Regenerate' : 'Generate with AI'}
                    </button>
                  </div>
                  {modelOptions.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Bot size={12} className="text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
                      <span className="text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary whitespace-nowrap">Model</span>
                      <div className="flex-1">
                        <Autocomplete
                          clearable
                          fullWidth
                          options={modelOptions}
                          value={selectedModel}
                          onChange={(val) => setSelectedModel(val || null)}
                          placeholder="Default (first available)"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {revisions.length > 0 && (
                  <div className="rounded-lg border border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/20 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-widest text-violet-600 dark:text-violet-400">
                        AI Draft {revisions.length > 1 && `(revision ${revisions.length})`}
                      </p>
                      {revisions.length > 1 && (
                        <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                          {revisions.length} revisions in history
                        </span>
                      )}
                    </div>
                    <TextField
                      multiline
                      rows={8}
                      size="small"
                      readOnly
                      fullWidth
                      value={revisions[revisions.length - 1].draft}
                      sx={{ input: 'resize-none' }}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={handleAcceptDraft}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors">
                        Accept
                      </button>
                      <button type="button" onClick={() => setShowGenFeedback(v => !v)}
                        className="px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary text-sm rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
                        Revise
                      </button>
                      <button type="button" onClick={handleDiscardDraft}
                        className="px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary text-sm rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
                        Discard
                      </button>
                    </div>
                    {showGenFeedback && (
                      <div className="space-y-2">
                        <TextField
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                          maxLength={500}
                          value={genFeedback}
                          onChange={e => setGenFeedback(e.target.value)}
                          placeholder="What should be changed? e.g. make it shorter, focus on remote work..."
                          sx={{ input: 'resize-none' }}
                        />
                        <button
                          type="button"
                          disabled={!genFeedback.trim() || generating}
                          onClick={() => handleGenerate(genFeedback)}
                          className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                        >
                          {generating ? 'Generating…' : 'Send feedback'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Post Content ──────────────────────────────────── */}
            <TextField
              label="Post Content"
              multiline
              rows={12}
              fullWidth
              size="small"
              value={form.full_post}
              onChange={e => setForm(prev => ({ ...prev, full_post: e.target.value }))}
              placeholder="Generated post content will appear here. You can also edit it manually."
              sx={{ input: 'resize-y leading-relaxed' }}
            />

          </div>
        </ModalFrame.Body>

          {/* ── Footer ─────────────────────────────────────────────── */}
          <ModalFrame.Footer className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
            <Button type="button" variant="text" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={saving || slugStatus === 'taken' || slugStatus === 'invalid' || checkingSlug}
              title={
                slugStatus === 'invalid'
                  ? 'Slug must contain Latin letters or digits'
                  : slugStatus === 'taken'
                  ? `Slug is already taken${slugExisting ? ` by post "${slugExisting.name}"` : ''} — choose another`
                  : checkingSlug ? 'Waiting for slug check to complete'
                  : undefined
              }
            >
              {saving ? 'Saving…' : checkingSlug ? 'Checking slug…' : isEdit ? 'Save Changes' : 'Create Post'}
            </Button>
          </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default TalentPostModal;
